
import { useState, useEffect } from 'react';
import Button from '../../../components/base/Button';
import QuoteModal from '../../../components/feature/QuoteModal';
import AppointmentModal from '../../../components/feature/AppointmentModal';

export default function Hero() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
  const [isAppointmentModalOpen, setIsAppointmentModalOpen] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  
  const slides = [
    {
      title: 'Sürdürülebilirlik Raporu Danışmanlığı',
      description: 'GRI ve TSRS Standartlarına Uyumlu Sürdürülebilirlik Raporu',
      image: 'https://readdy.ai/api/search-image?query=Professional%20business%20team%20working%20on%20sustainability%20reporting%20in%20modern%20office%20environment%20with%20green%20plants%20and%20natural%20lighting%2C%20corporate%20sustainability%20consulting%2C%20clean%20minimal%20background%2C%20business%20documents%20and%20charts%20visible&width=1200&height=600&seq=1&orientation=landscape'
    },
    {
      title: 'Karbon Ayak İzi Danışmanlığı',
      description: 'ISO 14064-1 Standardında Karbon Ayak İzi Hesaplama ve Raporlama',
      image: 'https://readdy.ai/api/search-image?query=Carbon%20footprint%20calculation%20concept%20with%20industrial%20facility%20and%20green%20technology%20elements%2C%20environmental%20consulting%20services%2C%20clean%20background%20with%20sustainability%20symbols%2C%20professional%20business%20setting&width=1200&height=600&seq=2&orientation=landscape'
    },
    {
      title: 'ESG Danışmanlığı',
      description: 'Çevresel, Sosyal ve Yönetişim Kriterlerinde Uzman Danışmanlık',
      image: 'https://readdy.ai/api/search-image?query=ESG%20consulting%20concept%20with%20environmental%20social%20governance%20elements%2C%20modern%20office%20setting%20with%20sustainability%20charts%20and%20green%20business%20indicators%2C%20professional%20consulting%20environment%2C%20clean%20minimal%20background&width=1200&height=600&seq=3&orientation=landscape'
    },
    {
      title: 'CBAM/SKDM Danışmanlığı',
      description: 'Sınırda Karbon Düzenleme Mekanizması Uyum Süreçleri',
      image: 'https://readdy.ai/api/search-image?query=European%20Union%20carbon%20border%20adjustment%20mechanism%20concept%20with%20EU%20flags%20and%20industrial%20symbols%2C%20international%20trade%20and%20sustainability%2C%20modern%20business%20consulting%20environment%2C%20clean%20professional%20background&width=1200&height=600&seq=4&orientation=landscape'
    },
    {
      title: 'Su Ayak İzi Danışmanlığı',
      description: 'ISO 14046 Su Ayak İzi Hesaplama ve Sürdürülebilir Su Yönetimi',
      image: 'https://readdy.ai/api/search-image?query=Water%20footprint%20consulting%20with%20clean%20water%20droplets%20and%20industrial%20water%20management%20systems%2C%20sustainability%20water%20conservation%20concept%2C%20modern%20environmental%20consulting%20office%2C%20professional%20clean%20background&width=1200&height=600&seq=5&orientation=landscape'
    }
  ];

  // Otomatik geçiş fonksiyonu
  useEffect(() => {
    if (!isPaused) {
      const timer = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
        setProgress(0);
      }, 6000);

      // Progress bar animasyonu
      const progressTimer = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 100) {
            return 0;
          }
          return prev + (100 / 600); // 6 saniyede 100% tamamlanacak
        });
      }, 10);

      return () => {
        clearInterval(timer);
        clearInterval(progressTimer);
      };
    }
  }, [isPaused, currentSlide, slides.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
    setProgress(0);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    setProgress(0);
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
    setProgress(0);
  };

  return (
    <>
      <section 
        className="relative h-screen overflow-hidden bg-gray-900"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        {/* Slides */}
        {slides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-all duration-1000 ease-in-out ${
              index === currentSlide 
                ? 'opacity-100 scale-100' 
                : index === (currentSlide - 1 + slides.length) % slides.length
                ? 'opacity-0 scale-105 -translate-x-full'
                : index === (currentSlide + 1) % slides.length
                ? 'opacity-0 scale-95 translate-x-full'
                : 'opacity-0 scale-110'
            }`}
          >
            <div
              className="absolute inset-0 bg-cover bg-center bg-no-repeat transition-transform duration-[8000ms] ease-out"
              style={{ 
                backgroundImage: `url('${slide.image}')`,
                transform: index === currentSlide ? 'scale(1.05)' : 'scale(1)'
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30"></div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
            </div>
            
            <div className="relative h-full flex items-center justify-center">
              <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                <div className={`transform transition-all duration-1000 delay-300 ${
                  index === currentSlide 
                    ? 'translate-y-0 opacity-100' 
                    : 'translate-y-8 opacity-0'
                }`}>
                  <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
                    <span className="inline-block animate-in slide-in-from-bottom-8 duration-700 delay-500">
                      {slide.title}
                    </span>
                  </h1>
                  <p className="text-xl md:text-2xl text-gray-200 mb-8 animate-in slide-in-from-bottom-4 duration-700 delay-700">
                    {slide.description}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center animate-in slide-in-from-bottom-4 duration-700 delay-900">
                    <Button 
                      size="lg" 
                      className="bg-emerald-600 hover:bg-emerald-700 px-8 py-4 text-lg whitespace-nowrap transform hover:scale-105 transition-all duration-300 hover:shadow-xl hover:shadow-emerald-500/25"
                      onClick={() => setIsQuoteModalOpen(true)}
                    >
                      <div className="w-5 h-5 flex items-center justify-center mr-2">
                        <i className="ri-send-plane-line"></i>
                      </div>
                      Ücretsiz Teklif Al
                    </Button>
                    <Button 
                      size="lg" 
                      variant="outline" 
                      className="border-white text-white hover:bg-white hover:text-gray-900 px-8 py-4 text-lg whitespace-nowrap transform hover:scale-105 transition-all duration-300 hover:shadow-xl hover:shadow-white/25"
                      onClick={() => setIsAppointmentModalOpen(true)}
                    >
                      <div className="w-5 h-5 flex items-center justify-center mr-2">
                        <i className="ri-calendar-line"></i>
                      </div>
                      Randevu Al
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating Elements */}
            <div className={`absolute inset-0 pointer-events-none ${
              index === currentSlide ? 'opacity-20' : 'opacity-0'
            } transition-opacity duration-1000`}>
              <div className="absolute top-20 left-20 w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></div>
              <div className="absolute top-40 right-32 w-1 h-1 bg-white rounded-full animate-ping" style={{ animationDelay: '1s' }}></div>
              <div className="absolute bottom-32 left-16 w-3 h-3 bg-emerald-300 rounded-full animate-bounce" style={{ animationDelay: '2s' }}></div>
              <div className="absolute bottom-48 right-20 w-1.5 h-1.5 bg-white rounded-full animate-pulse" style={{ animationDelay: '3s' }}></div>
            </div>
          </div>
        ))}

        {/* Navigation Arrows */}
        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 -translate-y-1/2 w-14 h-14 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-all duration-300 group border border-white/20 hover:border-white/40 hover:scale-110"
        >
          <i className="ri-arrow-left-line text-xl group-hover:scale-110 transition-transform group-hover:-translate-x-0.5"></i>
        </button>
        
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 -translate-y-1/2 w-14 h-14 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-all duration-300 group border border-white/20 hover:border-white/40 hover:scale-110"
        >
          <i className="ri-arrow-right-line text-xl group-hover:scale-110 transition-transform group-hover:translate-x-0.5"></i>
        </button>

        {/* Slide Indicators */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex space-x-3">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`relative transition-all duration-300 ${
                index === currentSlide 
                  ? 'w-8 h-3' 
                  : 'w-3 h-3 hover:w-4'
              }`}
            >
              <div className={`absolute inset-0 rounded-full transition-all duration-300 ${
                index === currentSlide 
                  ? 'bg-emerald-500 shadow-lg shadow-emerald-500/50' 
                  : 'bg-white/50 hover:bg-white/70'
              }`} />
              {index === currentSlide && (
                <div 
                  className="absolute inset-0 bg-gradient-to-r from-emerald-400 to-emerald-600 rounded-full transition-all duration-300"
                  style={{ 
                    width: `${progress}%`,
                    maxWidth: '100%'
                  }}
                />
              )}
            </button>
          ))}
        </div>

        {/* Progress Bar */}
        <div className="absolute bottom-0 left-0 w-full h-1 bg-black/20">
          <div 
            className="h-full bg-gradient-to-r from-emerald-400 via-emerald-500 to-emerald-600 transition-all duration-100 ease-linear relative overflow-hidden"
            style={{ width: `${progress}%` }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-pulse"></div>
          </div>
        </div>

        {/* Slide Counter */}
        <div className="absolute top-8 right-8 bg-black/30 backdrop-blur-sm rounded-full px-4 py-2 text-white text-sm font-medium">
          <span className="text-emerald-400">{(currentSlide + 1).toString().padStart(2, '0')}</span>
          <span className="text-white/60 mx-1">/</span>
          <span className="text-white/80">{slides.length.toString().padStart(2, '0')}</span>
        </div>

        {/* Pause/Play Button */}
        <button
          onClick={() => setIsPaused(!isPaused)}
          className="absolute top-8 left-8 w-10 h-10 bg-black/30 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/40 transition-all duration-300 group"
        >
          <i className={`${isPaused ? 'ri-play-line' : 'ri-pause-line'} text-lg group-hover:scale-110 transition-transform`}></i>
        </button>

        {/* Slide Title Overlay */}
        <div className="absolute bottom-20 left-8 max-w-md">
          <div className="bg-gradient-to-r from-black/60 to-transparent backdrop-blur-sm rounded-r-2xl pr-8 py-4 pl-6">
            <h3 className="text-white font-semibold text-lg mb-1">
              {slides[currentSlide].title}
            </h3>
            <p className="text-gray-300 text-sm">
              {slides[currentSlide].description}
            </p>
          </div>
        </div>
      </section>

      {/* Modals */}
      <QuoteModal 
        isOpen={isQuoteModalOpen} 
        onClose={() => setIsQuoteModalOpen(false)} 
      />
      <AppointmentModal 
        isOpen={isAppointmentModalOpen} 
        onClose={() => setIsAppointmentModalOpen(false)} 
      />
    </>
  );
}
